import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminFetchRevenue, type RevenueRow } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// Pricing — kept as frontend constants. If pricing needs to be server-driven
// later, move to /admin/revenue response.
const PRICE_PER_TOOL_CALL = 5 / 1000; // 1000 Tool Calls = 5,00 €
const PRICE_PER_PROMPT_TOKEN = 0.0015 / 1000; // 1000 Input Tokens = 0,0015 €
const PRICE_PER_COMPLETION_TOKEN = 0.008 / 1000; // 1000 Output Tokens = 0,008 €

type Range = { from: string; to: string };

function defaultRange(days = 30): Range {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

const eur = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const intFmt = new Intl.NumberFormat("de-DE");

type Computed = RevenueRow & {
  promptCost: number;
  completionCost: number;
  tokenCost: number;
  searchCost: number;
  totalCost: number;
};

function compute(rows: RevenueRow[]): Computed[] {
  return rows.map((r) => {
    const promptCost = r.prompt_tokens * PRICE_PER_PROMPT_TOKEN;
    const completionCost = r.completion_tokens * PRICE_PER_COMPLETION_TOKEN;
    const tokenCost = promptCost + completionCost;
    const searchCost = r.tool_calls * PRICE_PER_TOOL_CALL;
    return {
      ...r,
      promptCost,
      completionCost,
      tokenCost,
      searchCost,
      totalCost: tokenCost + searchCost,
    };
  });
}

function sum(rows: Computed[], key: keyof Computed): number {
  return rows.reduce((acc, r) => acc + (r[key] as number), 0);
}

export function AdminRevenueRoute() {
  const { me } = useAuth();
  const [range, setRange] = useState<Range>(() => defaultRange(30));

  const revenue = useQuery({
    queryKey: ["admin", "revenue", range],
    queryFn: () =>
      adminFetchRevenue({
        from: `${range.from}T00:00:00Z`,
        to: `${range.to}T23:59:59Z`,
      }),
    enabled: Boolean(me?.is_admin),
  });

  const computed = useMemo(
    () => (revenue.data ? compute(revenue.data.rows) : []),
    [revenue.data],
  );

  const totals = useMemo(
    () => ({
      promptCost: sum(computed, "promptCost"),
      completionCost: sum(computed, "completionCost"),
      tokenCost: sum(computed, "tokenCost"),
      searchCost: sum(computed, "searchCost"),
      totalCost: sum(computed, "totalCost"),
    }),
    [computed],
  );

  if (!me?.is_admin) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm">
        Kein Admin-Zugriff.
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ertragsübersicht</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="rev-from">Von</Label>
            <Input
              id="rev-from"
              type="date"
              value={range.from}
              onChange={(e) =>
                setRange((r) => ({ ...r, from: e.target.value }))
              }
              className="w-40"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rev-to">Bis</Label>
            <Input
              id="rev-to"
              type="date"
              value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="w-40"
            />
          </div>
        </div>

        <div className="rounded-md border bg-muted/40 p-4 text-xs text-muted-foreground">
          Preise: 1.000 Tool-Calls = {eur.format(5)} · 1.000 Input-Tokens ={" "}
          {eur.format(0.0015)} · 1.000 Output-Tokens = {eur.format(0.008)}
        </div>

        {revenue.isLoading && <p className="text-sm">Lade…</p>}
        {revenue.isError && (
          <p className="text-sm text-destructive">
            Konnte Erträge nicht laden.
          </p>
        )}

        {revenue.data && computed.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Keine Nutzer im System.
          </p>
        )}

        {revenue.data && computed.length > 0 && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2">Nutzer</th>
                  <th className="px-3 py-2 text-right">Runs</th>
                  <th className="px-3 py-2 text-right">Tool-Calls</th>
                  <th className="px-3 py-2 text-right">Input-Tokens</th>
                  <th className="px-3 py-2 text-right">Output-Tokens</th>
                  <th className="px-3 py-2 text-right">Input-Kosten</th>
                  <th className="px-3 py-2 text-right">Output-Kosten</th>
                  <th className="px-3 py-2 text-right">Token-Kosten</th>
                  <th className="px-3 py-2 text-right">Suchkosten</th>
                  <th className="px-3 py-2 text-right">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {computed.map((r) => (
                  <tr key={r.user_id} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.display_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.email}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {intFmt.format(r.run_count)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {intFmt.format(r.tool_calls)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {intFmt.format(r.prompt_tokens)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {intFmt.format(r.completion_tokens)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {eur.format(r.promptCost)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {eur.format(r.completionCost)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {eur.format(r.tokenCost)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {eur.format(r.searchCost)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {eur.format(r.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/60">
                <tr className="border-t">
                  <td className="px-3 py-2 font-semibold" colSpan={5}>
                    Summe
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {eur.format(totals.promptCost)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {eur.format(totals.completionCost)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {eur.format(totals.tokenCost)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {eur.format(totals.searchCost)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {eur.format(totals.totalCost)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
